import React from "react";
import {
  BarChart3,
  BookOpenText,
  FileText,
  SpellCheck,
  WholeWord,
} from "lucide-react";

const formatScore = (score) => (score || score === 0 ? score : "N/A");

export default function ScoreCardForWriting(props) {
  const maxScore = props.maxScore || 7;
  const contentMaxScore = props.contentMaxScore || 2;
  const formMaxScore = props.formMaxScore || 1;
  const grammarMaxScore = props.grammarMaxScore || 2;
  const vocabularyMaxScore = props.vocabularyMaxScore || 2;
  const spellingMaxScore = props.spellingMaxScore || 2;
  const hasSeparateSpelling = props.spellingScore || props.spellingScore === 0;

  const grammarRows = hasSeparateSpelling
    ? [
        {
          label: "Grammar",
          value: props.grammarScore ?? props.grammarSpellingScore,
          max: grammarMaxScore,
          icon: SpellCheck,
          tone: "bg-purple-50 text-purple-700 ring-purple-100",
        },
        {
          label: "Spelling",
          value: props.spellingScore,
          max: spellingMaxScore,
          icon: SpellCheck,
          tone: "bg-rose-50 text-rose-700 ring-rose-100",
        },
      ]
    : [
        {
          label: "Grammar & spelling",
          value: props.grammarSpellingScore,
          max: grammarMaxScore,
          icon: SpellCheck,
          tone: "bg-purple-50 text-purple-700 ring-purple-100",
        },
      ];

  const rows = [
    {
      label: "Content",
      value: props.contentScore,
      max: contentMaxScore,
      icon: BookOpenText,
      tone: "bg-blue-50 text-blue-700 ring-blue-100",
    },
    {
      label: "Form",
      value: props.formScore,
      max: formMaxScore,
      icon: FileText,
      tone: "bg-green-50 text-green-700 ring-green-100",
    },
    ...grammarRows,
    {
      label: "Vocabulary",
      value: props.vocabularyScore,
      max: vocabularyMaxScore,
      icon: WholeWord,
      tone: "bg-orange-50 text-orange-700 ring-orange-100",
    },
    props.coherenceScore || props.coherenceScore === 0
      ? {
          label: "Coherence",
          value: props.coherenceScore,
          max: 2,
          icon: BarChart3,
          tone: "bg-cyan-50 text-cyan-700 ring-cyan-100",
        }
      : null,
    props.developmentScore || props.developmentScore === 0
      ? {
          label: "Development",
          value: props.developmentScore,
          max: 2,
          icon: BookOpenText,
          tone: "bg-amber-50 text-amber-700 ring-amber-100",
        }
      : null,
    props.structureScore || props.structureScore === 0
      ? {
          label: "Structure",
          value: props.structureScore,
          max: 1,
          icon: FileText,
          tone: "bg-rose-50 text-rose-700 ring-rose-100",
        }
      : null,
  ].filter(Boolean);

  return (
    <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
      <div className="grid gap-0 lg:grid-cols-[minmax(260px,0.9fr),1.1fr]">
        <div className="bg-violet-600 p-6 text-white">
          <div className="flex items-center justify-between gap-3">
            <div>
              <p className="text-sm font-semibold uppercase text-violet-100">
                Total score
              </p>
              <h3 className="mt-1 text-2xl font-extrabold">Writing score</h3>
            </div>
            <span className="grid h-11 w-11 place-items-center rounded-md bg-white/15">
              <BarChart3 className="h-5 w-5" aria-hidden="true" />
            </span>
          </div>

          <div className="mt-8 flex items-end gap-2">
            <span className="text-6xl font-extrabold leading-none">
              {formatScore(props.finalScore)}
            </span>
            <span className="pb-2 text-sm font-bold text-violet-100">
              / {maxScore}
            </span>
          </div>

          <div className="mt-6 rounded-md bg-white/10 p-4">
            <p className="text-xs font-semibold uppercase text-violet-100">
              Remarks
            </p>
            <p className="mt-1 text-xl font-extrabold">
              {props.remarks || "Awaiting feedback"}
            </p>
          </div>
        </div>

        <div className="p-5 sm:p-6">
          <p className="text-sm font-semibold uppercase text-purple-700">
            Breakdown
          </p>
          <h4 className="mt-1 text-xl font-extrabold text-gray-900">
            Writing performance
          </h4>

          <div className="mt-5 grid gap-3">
            {rows.map((row) => {
              const Icon = row.icon;

              return (
                <div
                  key={row.label}
                  className="flex items-center justify-between gap-3 rounded-md border border-slate-200 bg-slate-50 p-4"
                >
                  <div className="flex items-center gap-3">
                    <span
                      className={`grid h-10 w-10 shrink-0 place-items-center rounded-md ring-1 ${row.tone}`}
                    >
                      <Icon className="h-5 w-5" aria-hidden="true" />
                    </span>
                    <span className="font-bold text-gray-800">{row.label}</span>
                  </div>
                  <span className="text-lg font-extrabold text-gray-900">
                    {formatScore(row.value)}/{row.max}
                  </span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
